import {
  FiActivity,
  FiBriefcase,
  FiMessageSquare,
  FiUsers,
} from "react-icons/fi";
import { JobSummaryChart, OverviewChart } from "../components/charts";
import { fetchApplications, fetchJobs } from "../redux/careers";
import { useDispatch, useSelector } from "react-redux";

import { Layout } from "../components/section";
import { Title } from "../components/typography";
import { fetchContacts } from "../redux/contact";
import { fetchServices } from "../redux/services";
import { keyframes } from "styled-components";
import styled from "styled-components";
import { useEffect } from "react";

export const Dashboard = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchJobs());
    dispatch(fetchApplications());
    dispatch(fetchContacts());
    dispatch(fetchServices());
  }, [dispatch]);

  const { user } = useSelector(
    (state) => state.user || { user: { given_name: "Admin" } },
  );

  const { jobs, applications } = useSelector(
    (state) => state.careers || { jobs: [], applications: [] },
  );
  const { contacts } = useSelector(
    (state) => state.contacts || { contacts: [] },
  );
  const { services } = useSelector(
    (state) => state.services || { services: [] },
  );

  return (
    <Layout>
      <DashboardLayout>
        {/* 1. GREETING HEADER */}
        <HeaderSection>
          <div>
            <Greeting number="1">
              Welcome back, {user?.given_name || "Admin"}!
            </Greeting>
            <SubGreeting>
              Here's an overview of your organization's progress today.
            </SubGreeting>
          </div>
        </HeaderSection>

        {/* 2. QUICK STATS ROW */}
        <StatsGrid>
          <StatCard delay="0.1s">
            <StatHeader>
              <IconWrapper bg="#E6F2FF" color="#3B5BDB">
                <FiBriefcase />
              </IconWrapper>
              <Trend positive>+12%</Trend>
            </StatHeader>
            <StatValue>{jobs?.length || 0}</StatValue>
            <StatLabel>Active Jobs</StatLabel>
          </StatCard>

          <StatCard delay="0.2s">
            <StatHeader>
              <IconWrapper bg="#E8F8F5" color="#2ECA7F">
                <FiUsers />
              </IconWrapper>
              <Trend positive>+5%</Trend>
            </StatHeader>
            <StatValue>{applications?.length || 0}</StatValue>
            <StatLabel>Total Applicants</StatLabel>
          </StatCard>

          <StatCard delay="0.3s">
            <StatHeader>
              <IconWrapper bg="#FFF4E6" color="#F39C12">
                <FiMessageSquare />
              </IconWrapper>
              <Trend negative>-2%</Trend>
            </StatHeader>
            <StatValue>
              {contacts?.filter((c) => !c.read).length || 0}
            </StatValue>
            <StatLabel>Unread Messages</StatLabel>
          </StatCard>

          <StatCard delay="0.4s">
            <StatHeader>
              <IconWrapper bg="#F4ECF7" color="#9B59B6">
                <FiActivity />
              </IconWrapper>
              <Trend positive>+8%</Trend>
            </StatHeader>
            <StatValue>{services?.length || 0}</StatValue>
            <StatLabel>Active Services</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* 3. MAIN CHARTS WIDGETS */}
        <WidgetsGrid>
          <WidgetCard delay="0.5s">
            {/* Reusing your dynamic OverviewChart */}
            <OverviewChart jobs={jobs} applications={applications} />
          </WidgetCard>

          <WidgetCard delay="0.6s">
            {/* Reusing your dynamic JobSummaryChart */}
            <JobSummaryChart jobs={jobs} />
          </WidgetCard>
        </WidgetsGrid>
      </DashboardLayout>
    </Layout>
  );
};

export default Dashboard;

// --- ANIMATIONS ---
const slideUpFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DashboardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem 0;
`;

/* HEADER */
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  animation: ${slideUpFade} 0.5s ease-out forwards;
`;

const Greeting = styled(Title)`
  font-size: 28px;
  color: #111827;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

const SubGreeting = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
`;

/* QUICK STATS */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03); /* Softer, wider shadow */
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.02);

  /* The Veyra Staggered Animation Effect */
  opacity: 0;
  animation: ${slideUpFade} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${(props) => props.delay || "0s"};

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const Trend = styled.span`
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 50px;
  background-color: ${(props) => (props.positive ? "#E8F8F5" : "#FDEDEC")};
  color: ${(props) => (props.positive ? "#2ECA7F" : "#E74C3C")};
`;

const StatValue = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px 0;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
`;

/* MAIN WIDGETS */
const WidgetsGrid = styled.div`
  display: grid;
  /* Replicates the asymmetric split from the My Organization image */
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr; /* Stack on smaller screens */
  }
`;

const WidgetCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.02);

  /* Animation */
  opacity: 0;
  animation: ${slideUpFade} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${(props) => props.delay || "0s"};
`;
